import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import Thermometer from "../components/Thermometer";
import { formatEUR, formatMonth } from "../lib/format";
import { ArrowUpRight, Warning, Clock, Coins, TrendUp, CurrencyEur, Scales, CaretLeft, CaretRight, Hourglass, ChartPieSlice, Target, Calculator, CaretDown, CaretUp, Envelope } from "@phosphor-icons/react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [breakdown, setBreakdown] = useState(null);
  const [modal, setModal] = useState(null); // {kind, title, subtitle, items}

  const load = useCallback(async () => {
    try {
      const [d, b] = await Promise.all([
        api.get("/dashboard"),
        api.get("/dashboard/breakdown"),
      ]);
      setData(d.data);
      setBreakdown(b.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openBreakdown = (kind) => {
    if (!breakdown) return;
    const map = {
      received: { title: "Eingang aktueller Monat", subtitle: `Bereits vereinnahmt in ${breakdown.current_ym}`, items: breakdown.received_month || [] },
      expected_month: { title: "Erwartet diesen Monat", subtitle: "Rechnungen, die naturgemäß in diesem Monat fällig sind", items: breakdown.expected_month_pure || [] },
      expected_open: { title: "Erwartet offen gesamt", subtitle: "Alle unbezahlten AVGS + FBW mit Fälligkeitsdatum", items: breakdown.expected_open || [] },
      unsent: { title: "Rechnung zu versenden", subtitle: "Kurs beendet, Rechnung noch nicht rausgeschickt", items: breakdown.unsent || [] },
      overdue: { title: "Überfällig", subtitle: "Rechnung versendet + Fälligkeit überschritten", items: breakdown.overdue || [] },
    };
    const m = map[kind];
    if (m) setModal({ kind, ...m });
  };

  if (loading) {
    return <div className="text-sm text-[#5B6371]" data-testid="dashboard-loading">Lade Daten …</div>;
  }
  if (!data) return null;

  const cm = data.current_month;
  const t = data.totals;
  const profitTargetCum = cm.revenue_target_monthly;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Liquiditäts-Dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1F242E] mt-1.5">
            {formatMonth(cm.ym)}
          </h1>
          <p className="text-sm text-[#5B6371] mt-1">Aktuelle Übersicht zu Zahlungseingängen, Break-even und Gewinn.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#5B6371]">
          <span className="inline-block h-2 w-2 rounded-full bg-[#0AA09E]"></span>
          Live-Daten
        </div>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
        <KpiCard label="Eingang aktueller Monat" value={formatEUR(cm.income)} icon={Coins}
          testId="kpi-current-income" sub={`Ziel: ${formatEUR(cm.revenue_target_monthly)}`}
          onClick={() => openBreakdown("received")} />
        <KpiCard label="Erwartet diesen Monat" value={formatEUR(cm.expected_this_month_pure || 0)} icon={TrendUp}
          testId="kpi-expected-month"
          sub={cm.overdue_rolled_into_month > 0
            ? `rein · +${formatEUR(cm.overdue_rolled_into_month)} rolliert aus Überfällig`
            : `AVGS ${formatEUR(cm.expected_this_month_pure_avgs || 0)} · FBW ${formatEUR(cm.expected_this_month_pure_fbw || 0)}`}
          onClick={() => openBreakdown("expected_month")} />
        <KpiCard label="Erwartet offen gesamt" value={formatEUR(t.total_expected_open || 0)} icon={Clock}
          testId="kpi-expected-total"
          sub={`AVGS ${formatEUR(t.total_expected_open_avgs || 0)} · FBW ${formatEUR(t.total_expected_open_fbw || 0)}`}
          onClick={() => openBreakdown("expected_open")} />
        <KpiCard label="Rechnung zu versenden" value={formatEUR(t.unsent_amount || 0)} icon={Envelope}
          testId="kpi-unsent" sub={`${t.unsent_invoices || 0} Kurse beendet, Rechnung fehlt`} warn={(t.unsent_invoices || 0) > 0}
          onClick={() => openBreakdown("unsent")} />
        <KpiCard label="Überfällig" value={formatEUR(t.overdue_amount)} icon={Warning}
          testId="kpi-overdue" sub={`${t.overdue_invoices} Rechnungen versendet & fällig`} warn
          onClick={() => openBreakdown("overdue")} />
      </div>

      <BreakdownModal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.title}
        subtitle={modal?.subtitle}
        items={modal?.items || []}
        kind={modal?.kind}
      />

      {/* Break-even UE Rechner — wieviele UEs brauche ich um Break-even zu erreichen? */}
      <BreakEvenUeCalculator data={data} />

      {/* Einnahmen vs Ausgaben — kompakt 12 Monate */}
      <IncomeVsExpensesChart forecast={data.forecast || []} currentYm={cm.ym} />

      {/* Liquiditätsvorschau */}
      <CollapsibleLiquidityForecast data={data} />

      {/* Vergleich: Fällige Rechnungen — Mit vs. Ohne rollierte Vergangenheit */}
      <DuePerMonthWidget forecast={data.forecast || []} currentYm={cm.ym} />

      {/* Erhalten gesamt KPI tile below forecast for context */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
        <KpiCard label="Erhalten gesamt" value={formatEUR(t.total_received_year)} icon={ArrowUpRight}
          testId="kpi-total-received" sub="Alle bisherigen Zahlungseingänge" />
        <KpiCard label="Erwartet nächste 30 Tage" value={formatEUR(t.upcoming_30d_amount)} icon={Clock}
          testId="kpi-upcoming" sub="Kurzfristige Liquidität" />
        <KpiCard label="Ausgaben diesen Monat" value={formatEUR(t.expenses_this_month || 0)} icon={CurrencyEur}
          testId="kpi-expenses-month" warn sub="Wiederkehrend + einmalig" />
        <KpiCard label="Saldo diesen Monat" value={formatEUR(t.balance_this_month || 0)} icon={Scales}
          testId="kpi-balance-month"
          warn={(t.balance_this_month || 0) < 0}
          sub={(t.balance_this_month || 0) >= 0 ? "Einnahme − Ausgabe (Überschuss)" : "Einnahme − Ausgabe (Defizit)"} />
        <KpiCard
          label="Ø Tage bis Zahlung"
          value={t.avg_payment_days !== null && t.avg_payment_days !== undefined ? `${t.avg_payment_days} Tage` : "—"}
          icon={Hourglass}
          testId="kpi-avg-payment-days"
          sub={t.avg_payment_days_samples ? `Aus ${t.avg_payment_days_samples} bezahlten Rechnungen` : "Noch keine bezahlten Rechnungen"} />
      </div>

      {/* Ein-/Auszahlungen letzte 4 Monate — wie Pivotal-Mockup */}
      <Last4MonthsBars
        rows={data.cashflow_last4 || []}
        avgNet={t.avg_net_last4 || 0}
      />

      {/* Cashflow-Simulator: Zeitraum-Filter Einnahmen vs Ausgaben */}
      <CashflowSimulator forecast={data.forecast || []} currentYm={cm.ym} settings={data.settings || {}} />

      {/* Thermometers — basiert auf ERWARTETE Einnahmen aktuellen Monat (für Liquiditätsplanung) */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Thermometer
          label="Break-even (erwartet diesen Monat)"
          sublabel={cm.income > 0
            ? `Aktuell erhalten: ${formatEUR(cm.income)} · Erwartet: ${formatEUR(cm.expected_this_month || 0)}`
            : `Erwartet diesen Monat: ${formatEUR(cm.expected_this_month || 0)}`}
          value={cm.expected_this_month || 0}
          breakEven={cm.break_even}
          target={cm.revenue_target_monthly}
          testId="thermometer-breakeven"
        />
        <Thermometer
          label="Gewinn-Thermometer (erwartet)"
          sublabel={(() => {
            const exp = cm.expected_this_month || 0;
            const profit = exp - cm.break_even;
            return profit >= 0 ? `Erwarteter Gewinn: ${formatEUR(profit)}` : `Erwarteter Verlust: ${formatEUR(profit)}`;
          })()}
          value={cm.expected_this_month || 0}
          breakEven={cm.break_even}
          target={profitTargetCum}
          testId="thermometer-profit"
        />
      </section>

      {/* Deckungsbeitrag pro Maßnahme */}
      <ContributionMarginWidget />

      {/* Monthly table */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="label-eyebrow">Monatlicher Verlauf</div>
            <h2 className="font-display text-2xl font-semibold mt-1">Liquidität nach Monat</h2>
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="monthly-table">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB]">
                  <th className="text-left px-4 py-3 font-medium">Monat</th>
                  <th className="text-right px-4 py-3 font-medium">Eingang</th>
                  <th className="text-right px-4 py-3 font-medium">Kumuliert</th>
                  <th className="text-right px-4 py-3 font-medium">Umsatzziel kum.</th>
                  <th className="text-right px-4 py-3 font-medium">Abstand</th>
                  <th className="text-right px-4 py-3 font-medium">Gewinn (Ist)</th>
                </tr>
              </thead>
              <tbody>
                {data.monthly.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-[#94A3B8]">
                      Noch keine Zahlungen erfasst.
                    </td>
                  </tr>
                )}
                {data.monthly.map((m) => (
                  <tr key={m.month} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]">
                    <td className="px-4 py-2.5 font-medium">{formatMonth(m.month)}</td>
                    <td className="px-4 py-2.5 text-right">{formatEUR(m.income)}</td>
                    <td className="px-4 py-2.5 text-right">{formatEUR(m.cumulative)}</td>
                    <td className="px-4 py-2.5 text-right text-[#5B6371]">{formatEUR(m.revenue_target_cum)}</td>
                    <td className={`px-4 py-2.5 text-right ${m.gap_revenue > 0 ? "text-[#FF6B5C]" : "text-[#0AA09E]"}`}>
                      {m.gap_revenue > 0 ? "−" : "+"}{formatEUR(Math.abs(m.gap_revenue))}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-medium ${m.profit_ist >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}>
                      {formatEUR(m.profit_ist)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, warn, testId, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`bg-white border border-[#E5E7EB] rounded-lg p-5 transition-all ${
        clickable ? "cursor-pointer hover:border-[#0AA09E] hover:shadow-md" : "hover:border-[#E2E8F0]"
      }`}
      data-testid={testId}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            {label}
            {clickable && <span className="text-[9px] text-[#94A3B8]">▸ Details</span>}
          </div>
          <div className={`font-display text-2xl font-semibold mt-1.5 ${warn ? "text-[#FF6B5C]" : "text-[#1F242E]"}`}>
            {value}
          </div>
          {sub && <div className="text-xs text-[#94A3B8] mt-1">{sub}</div>}
        </div>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${warn ? "bg-[#FFF1EE] text-[#FF6B5C]" : "bg-[#E6F7F7] text-[#0AA09E]"}`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

function BreakdownModal({ open, onClose, title, subtitle, items, kind }) {
  if (!open) return null;
  const total = items.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="breakdown-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="label-eyebrow">Details</div>
            <div className="font-display font-bold text-lg mt-0.5">{title}</div>
            {subtitle && <div className="text-xs text-[#5B6371] mt-1">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#1F242E] px-3 py-1 rounded hover:bg-[#F1F5F9]" data-testid="breakdown-close">✕</button>
        </div>
        <div className="overflow-auto flex-1">
          {items.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-[#94A3B8]">Keine Einträge in dieser Kategorie.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F8FAFC]">
                <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b border-[#E5E7EB]">
                  <th className="text-left px-4 py-3 font-medium">Kunde / Kurs</th>
                  <th className="text-left px-4 py-3 font-medium">Typ</th>
                  <th className="text-left px-4 py-3 font-medium">{kind === "received" ? "Zahlungsdatum" : kind === "unsent" ? "Kursende" : "Fällig"}</th>
                  {kind === "overdue" && <th className="text-right px-4 py-3 font-medium">Tage überf.</th>}
                  <th className="text-right px-4 py-3 font-medium">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => (
                  <tr key={idx} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`breakdown-row-${idx}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-[#1F242E]">{r.customer_name || "—"}</div>
                      {r.measure_type && <div className="text-[10px] text-[#94A3B8]">{r.measure_type}</div>}
                      {r.detail && <div className="text-[10px] text-[#94A3B8]">{r.detail}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        r.source === "avgs" ? "bg-[#E6F7F7] text-[#0AA09E]" : r.source === "fbw" ? "bg-[#FFF1EE] text-[#FF6B5C]" : "bg-[#F1F5F9] text-[#5B6371]"
                      }`}>
                        {r.source === "avgs" ? (r.invoice_part === "mid" ? "AVGS Mid" : "AVGS") : r.source === "fbw" ? `FBW #${r.installment_index ?? ""}` : r.source}
                      </span>
                      {r.is_unsent && <div className="text-[9px] text-[#FF6B5C] mt-0.5">Rechnung fehlt</div>}
                    </td>
                    <td className="px-4 py-2.5 text-[#5B6371]">{r.date || r.mass_end_date || r.expected_payment_date || "—"}</td>
                    {kind === "overdue" && <td className="px-4 py-2.5 text-right text-[#FF6B5C] font-medium">{r.days_overdue ?? "—"}</td>}
                    <td className="px-4 py-2.5 text-right font-display font-bold text-[#1F242E]">{formatEUR(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#F8FAFC] border-t-2 border-[#0AA09E] font-display font-bold">
                  <td className="px-4 py-3" colSpan={kind === "overdue" ? 4 : 3}>Summe ({items.length})</td>
                  <td className="px-4 py-3 text-right text-[#0AA09E]" data-testid="breakdown-total">{formatEUR(total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ForecastTable({ forecast, breakEven, target, currentYm }) {
  const maxExp = Math.max(...forecast.map((r) => Math.max(r.expected || 0, r.actual || 0)), target || 1, 1);
  // Für Vergangenheits-Monate zeigen wir historische Sollwerte (Original-Monat, keine Roll-Forward, inkl. bezahlter Rechnungen).
  // Für aktuellen/zukünftige Monate zeigen wir den regulären Forecast (mit Roll-Forward überfälliger Beträge).
  const rowView = (row) => {
    const isPast = currentYm && row.month < currentYm;
    if (isPast) {
      return {
        expected: row.hist_expected || 0,
        expected_avgs: row.hist_expected_avgs || 0,
        expected_fbw: row.hist_expected_fbw || 0,
        expected_count: row.hist_expected_count || 0,
        isPast: true,
      };
    }
    return {
      expected: row.expected || 0,
      expected_avgs: row.expected_avgs || 0,
      expected_fbw: row.expected_fbw || 0,
      expected_count: row.expected_count || 0,
      isPast: false,
    };
  };
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="forecast-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
              <th className="text-left px-4 py-3 font-medium" rowSpan={2}>Monat</th>
              <th className="text-center px-4 py-2 font-medium border-l border-[#E5E7EB]" colSpan={3}>
                Soll (Erwartet)
              </th>
              <th className="text-center px-4 py-2 font-medium border-l border-[#E5E7EB]" colSpan={2}>
                Ist (Eingegangen)
              </th>
              <th className="text-center px-4 py-2 font-medium border-l border-[#E5E7EB]" colSpan={2}>
                Einnahme / Ausgabe
              </th>
              <th className="text-right px-4 py-3 font-medium border-l border-[#E5E7EB]" rowSpan={2}>Offen</th>
            </tr>
            <tr className="text-[10px] uppercase tracking-widest text-[#94A3B8] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
              <th className="text-right px-3 py-2 font-medium border-l border-[#E5E7EB]">AVGS</th>
              <th className="text-right px-3 py-2 font-medium">FBW</th>
              <th className="text-right px-3 py-2 font-medium">Gesamt</th>
              <th className="text-right px-3 py-2 font-medium border-l border-[#E5E7EB]">Betrag</th>
              <th className="text-right px-3 py-2 font-medium">Anz.</th>
              <th className="text-right px-3 py-2 font-medium border-l border-[#E5E7EB]">Ausgaben</th>
              <th className="text-right px-3 py-2 font-medium">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {forecast.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[#94A3B8]">
                Noch keine Rechnungen erfasst. Lege AVGS- oder FBW-Rechnungen an, um die Liquidität zu sehen.
              </td></tr>
            )}
            {forecast.map((row) => {
              const v = rowView(row);
              const open = v.expected - (row.actual || 0);
              const fullyPaid = v.expected > 0 && Math.abs(open) < 0.01;
              return (
                <tr key={row.month} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`forecast-row-${row.month}`}>
                  <td className="px-4 py-3 font-medium">
                    {formatMonth(row.month)}
                    {v.isPast && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[#94A3B8]">hist</span>}
                  </td>
                  <td className="px-3 py-3 text-right text-[#0AA09E] border-l border-[#F1F5F9]">
                    {formatEUR(v.expected_avgs)}
                  </td>
                  <td className="px-3 py-3 text-right text-[#3FB5B0]">{formatEUR(v.expected_fbw)}</td>
                  <td className="px-3 py-3 text-right font-display text-base font-semibold text-[#1F242E]">
                    {formatEUR(v.expected)}
                    <div className="text-[10px] text-[#94A3B8] font-body font-normal">
                      {v.expected_count} Rg.
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-medium text-[#0AA09E] border-l border-[#F1F5F9]" data-testid={`forecast-actual-${row.month}`}>
                    {formatEUR(row.actual || 0)}
                  </td>
                  <td className="px-3 py-3 text-right text-[#5B6371] text-xs">
                    {row.received_count || 0}×
                  </td>
                  <td className="px-3 py-3 text-right text-[#FF6B5C] border-l border-[#F1F5F9]">
                    {row.expenses > 0 ? `−${formatEUR(row.expenses)}` : <span className="text-[#94A3B8]">—</span>}
                  </td>
                  <td className={`px-3 py-3 text-right font-medium ${(row.balance ?? 0) >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}>
                    {row.expenses > 0 || row.actual > 0 ? formatEUR(row.balance) : "—"}
                  </td>
                  <td className={`px-4 py-3 text-right text-sm font-medium border-l border-[#F1F5F9] ${
                    fullyPaid ? "text-[#0AA09E]" : open > 0 ? "text-[#FF6B5C]" : "text-[#5B6371]"
                  }`}>
                    {fullyPaid ? "✓ bezahlt" : formatEUR(open)}
                  </td>
                </tr>
              );
            })}
            {forecast.length > 0 && (() => {
              // Summe: für Vergangenheit hist_*, für Zukunft normale expected*
              const sumAvgs = forecast.reduce((s, r) => s + rowView(r).expected_avgs, 0);
              const sumFbw = forecast.reduce((s, r) => s + rowView(r).expected_fbw, 0);
              const sumExpected = forecast.reduce((s, r) => s + rowView(r).expected, 0);
              const sumActual = forecast.reduce((s, r) => s + (r.actual || 0), 0);
              const sumCount = forecast.reduce((s, r) => s + (r.received_count || 0), 0);
              const sumExpenses = forecast.reduce((s, r) => s + (r.expenses || 0), 0);
              const sumBalance = forecast.reduce((s, r) => s + (r.balance || 0), 0);
              const sumOpen = sumExpected - sumActual;
              return (
                <tr className="bg-[#F8FAFC] border-t-2 border-[#E5E7EB] font-display font-bold">
                  <td className="px-4 py-3 text-sm">Summe</td>
                  <td className="px-3 py-3 text-right text-[#0AA09E] border-l border-[#F1F5F9]">
                    {formatEUR(sumAvgs)}
                  </td>
                  <td className="px-3 py-3 text-right text-[#3FB5B0]">
                    {formatEUR(sumFbw)}
                  </td>
                  <td className="px-3 py-3 text-right text-[#1F242E]">
                    {formatEUR(sumExpected)}
                  </td>
                  <td className="px-3 py-3 text-right text-[#0AA09E] border-l border-[#F1F5F9]">
                    {formatEUR(sumActual)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs text-[#5B6371]">
                    {sumCount}×
                  </td>
                  <td className="px-3 py-3 text-right text-[#FF6B5C] border-l border-[#F1F5F9]">
                    −{formatEUR(sumExpenses)}
                  </td>
                  <td className={`px-3 py-3 text-right ${
                    sumBalance >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"
                  }`}>
                    {formatEUR(sumBalance)}
                  </td>
                  <td className="px-4 py-3 text-right border-l border-[#F1F5F9] text-[#FF6B5C]">
                    {formatEUR(sumOpen)}
                  </td>
                </tr>
              );
            })()}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 bg-[#F8FAFC] border-t border-[#E5E7EB] flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[#5B6371]">
        <span><span className="inline-block w-3 h-3 rounded-sm bg-[#0AA09E] mr-1.5 align-middle"></span>AVGS = Rechnung nach Maßnahme</span>
        <span><span className="inline-block w-3 h-3 rounded-sm bg-[#3FB5B0] mr-1.5 align-middle"></span>FBW = Monatliche Abschläge</span>
        <span><strong>Soll</strong> — Vergangenheit: <span className="text-[#94A3B8]">hist</span> = Original-Sollwerte (was in dem Monat ursprünglich erwartet wurde). Zukunft: erwartet aus offenen Rechnungen.</span>
        <span><strong>Ist</strong> = manuell als bezahlt markiert oder als Zahlungseingang erfasst</span>
      </div>
    </div>
  );
}


function IncomeVsExpensesChart({ forecast, currentYm }) {
  const WINDOW = 12; // größeres Fenster: 12 Monate auf einen Blick

  // Alle Monate (inkl. Vergangenheit) sortiert
  const allRows = useMemo(() => {
    if (!forecast.length) return [];
    const sorted = [...forecast].sort((a, b) => a.month.localeCompare(b.month));
    return sorted.map((r) => {
      const income = r.month < currentYm ? (r.actual || 0) : Math.max(r.expected || 0, r.actual || 0);
      const expenses = r.expenses || 0;
      const balance = income - expenses;
      return {
        month: r.month,
        label: formatMonth(r.month).replace(/ 20(\d{2})/, " ’$1"),
        income: Math.round(income * 100) / 100,
        expenses: Math.round(expenses * 100) / 100,
        balance: Math.round(balance * 100) / 100,
      };
    });
  }, [forecast, currentYm]);

  const maxStart = Math.max(0, allRows.length - WINDOW);
  // Default: Fenster zentriert um aktuellen Monat, aber nach unten begrenzt
  const defaultStart = useMemo(() => {
    if (!allRows.length) return 0;
    const curIdx = allRows.findIndex((r) => r.month === currentYm);
    if (curIdx < 0) return maxStart;
    // 2 Monate Vergangenheit + 10 Monate Zukunft = 12 window, mit Fokus auf jetzt
    return Math.min(maxStart, Math.max(0, curIdx - 2));
  }, [allRows, currentYm, maxStart]);
  const [windowStart, setWindowStart] = useState(defaultStart);
  useEffect(() => { setWindowStart(defaultStart); }, [defaultStart]);

  const effectiveStart = Math.min(windowStart, maxStart);
  const rows = allRows.slice(effectiveStart, effectiveStart + WINDOW);

  // Preset-Sprünge
  const jumpTo = (preset) => {
    if (!allRows.length) return;
    if (preset === "all-past") {
      setWindowStart(0);
    } else if (preset === "year-2026") {
      const idx = allRows.findIndex((r) => r.month.startsWith("2026-01"));
      setWindowStart(Math.max(0, idx >= 0 ? idx : 0));
    } else if (preset === "current") {
      setWindowStart(defaultStart);
    } else if (preset === "future") {
      setWindowStart(maxStart);
    }
  };

  if (!allRows.length) return null;
  const totals = rows.reduce(
    (acc, r) => ({ income: acc.income + r.income, expenses: acc.expenses + r.expenses, balance: acc.balance + r.balance }),
    { income: 0, expenses: 0, balance: 0 }
  );
  const fromLabel = rows[0]?.label || "—";
  const toLabel = rows[rows.length - 1]?.label || "—";

  return (
    <section data-testid="income-vs-expenses-chart">
      <div className="flex flex-wrap items-end justify-between mb-3 gap-3">
        <div>
          <div className="label-eyebrow">Cashflow-Übersicht</div>
          <h2 className="font-display text-2xl font-bold mt-1">Einnahmen vs. Ausgaben</h2>
          <p className="text-xs text-[#5B6371] mt-1">
            {WINDOW}-Monats-Fenster · <strong>{fromLabel} – {toLabel}</strong> · Einnahmen (Ist bis heute, danach erwartet) vs. Ausgaben · Saldo
          </p>
        </div>
        <div className="flex gap-3 text-right">
          <MiniStat label="Σ Einnahmen" value={formatEUR(totals.income)} color="#0AA09E" testId="cashflow-sum-income" />
          <MiniStat label="Σ Ausgaben" value={formatEUR(totals.expenses)} color="#FF6B5C" testId="cashflow-sum-expenses" />
          <MiniStat label="Σ Saldo" value={formatEUR(totals.balance)} color={totals.balance >= 0 ? "#0AA09E" : "#FF6B5C"} testId="cashflow-sum-balance" />
        </div>
      </div>

      {/* Preset-Buttons + Slider */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 md:p-4 mb-2">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-[#5B6371] mr-1">Schnellsprung:</span>
          <button type="button" onClick={() => jumpTo("year-2026")} data-testid="cashflow-preset-2026"
            className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] bg-white hover:border-[#0AA09E] hover:text-[#0AA09E]">
            Ab Jan 2026
          </button>
          <button type="button" onClick={() => jumpTo("all-past")} data-testid="cashflow-preset-past"
            className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] bg-white hover:border-[#0AA09E] hover:text-[#0AA09E]">
            Frühestes Fenster
          </button>
          <button type="button" onClick={() => jumpTo("current")} data-testid="cashflow-preset-current"
            className="text-xs px-2.5 py-1 rounded-md border border-[#0AA09E] bg-[#E6F7F7] text-[#0AA09E]">
            Jetzt (aktuell)
          </button>
          <button type="button" onClick={() => jumpTo("future")} data-testid="cashflow-preset-future"
            className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] bg-white hover:border-[#0AA09E] hover:text-[#0AA09E]">
            Zukunft
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setWindowStart((v) => Math.max(0, v - 1))}
            disabled={effectiveStart === 0}
            data-testid="cashflow-prev"
            className="p-2 rounded-md border border-[#E5E7EB] hover:bg-[#F8FAFC] disabled:opacity-40 disabled:cursor-not-allowed"
            title="Früher"
          >
            <CaretLeft size={16} />
          </button>
          <input
            type="range"
            min={0}
            max={maxStart}
            step={1}
            value={effectiveStart}
            onChange={(e) => setWindowStart(parseInt(e.target.value, 10))}
            data-testid="cashflow-slider"
            className="flex-1 accent-[#0AA09E] cursor-pointer"
          />
          <button
            type="button"
            onClick={() => setWindowStart((v) => Math.min(maxStart, v + 1))}
            disabled={effectiveStart >= maxStart}
            data-testid="cashflow-next"
            className="p-2 rounded-md border border-[#E5E7EB] hover:bg-[#F8FAFC] disabled:opacity-40 disabled:cursor-not-allowed"
            title="Später"
          >
            <CaretRight size={16} />
          </button>
          <div className="hidden md:block text-xs text-[#5B6371] min-w-[110px] text-right" data-testid="cashflow-range-label">
            {fromLabel} – {toLabel}
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 md:p-5">
        <div style={{ width: "100%", height: 360 }}>
          <ResponsiveContainer>
            <ComposedChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5B6371" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} interval={0} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1000 || v <= -1000 ? `${Math.round(v / 1000)}k` : v} />
              <Tooltip
                cursor={{ fill: "rgba(10,160,158,0.05)" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
                formatter={(val, name) => [formatEUR(val), name]}
                labelFormatter={(label, payload) => {
                  const row = payload?.[0]?.payload;
                  return row ? formatMonth(row.month) : label;
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
              <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="2 2" />
              <Bar dataKey="income" name="Einnahmen (erwartet/ist)" fill="#0AA09E" radius={[4, 4, 0, 0]} barSize={28} />
              <Bar dataKey="expenses" name="Ausgaben" fill="#FF6B5C" radius={[4, 4, 0, 0]} barSize={28} />
              <Line dataKey="balance" name="Saldo" stroke="#1E40AF" strokeWidth={2.5}
                dot={renderBalanceDot}
                activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value, color, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#94A3B8]">{label}</div>
      <div className="font-display font-bold text-lg md:text-xl" style={{ color }}>{value}</div>
    </div>
  );
}

const renderBalanceDot = (props) => {
  const { cx, cy, payload, index } = props;
  if (cx === null || cy === null || cx === undefined || cy === undefined) return null;
  const fill = (payload?.balance || 0) >= 0 ? "#0AA09E" : "#FF6B5C";
  return <circle key={`dot-${index}`} cx={cx} cy={cy} r={4} fill={fill} stroke="#fff" strokeWidth={2} />;
};


function Last4MonthsBars({ rows, avgNet }) {
  if (!rows.length) return null;
  const max = Math.max(...rows.flatMap((r) => [r.income, r.expenses]), 1);
  const labelFor = (ym) => {
    const [y, m] = ym.split("-");
    const names = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];
    return `${names[parseInt(m, 10) - 1]} ’${y.slice(2)}`;
  };
  return (
    <section data-testid="last4-bars" className="bg-white border border-[#E5E7EB] rounded-lg p-5 md:p-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Ein- / Auszahlungen</div>
          <div className={`font-display text-4xl md:text-5xl font-bold mt-2 ${avgNet >= 0 ? "text-[#1F242E]" : "text-[#FF6B5C]"}`} data-testid="last4-avg-net">
            {formatEUR(avgNet)}
          </div>
          <div className="text-xs text-[#5B6371] mt-1">Ø Saldo (netto) · {labelFor(rows[0].month)} – {labelFor(rows[rows.length - 1].month)}</div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded-full bg-[#0AA09E]"></div>
            <span className="text-[#5B6371]">Einnahmen gesamt</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded-full bg-[#FF6B5C]"></div>
            <span className="text-[#5B6371]">Ausgaben gesamt</span>
          </div>
        </div>
      </div>
      {/* Bar Group */}
      <div className="mt-6 grid grid-cols-4 gap-3 md:gap-6 items-end" style={{ minHeight: 220 }}>
        {rows.map((r) => {
          const incH = Math.round((r.income / max) * 180);
          const expH = Math.round((r.expenses / max) * 180);
          return (
            <div key={r.month} className="flex flex-col items-center" data-testid={`last4-col-${r.month}`}>
              <div className="flex items-end gap-2 h-[200px]">
                <div className="flex flex-col items-center">
                  <div className="text-[10px] font-display font-semibold text-[#1F242E] mb-1">
                    {r.income >= 1000 ? `${(r.income / 1000).toFixed(1)}K` : Math.round(r.income)}
                  </div>
                  <div
                    className="w-7 md:w-10 bg-[#0AA09E] rounded-t-md transition-all"
                    style={{ height: `${Math.max(incH, 2)}px` }}
                    title={`Einnahmen: ${formatEUR(r.income)}`}
                  />
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-[10px] font-display font-semibold text-[#1F242E] mb-1">
                    {r.expenses >= 1000 ? `${(r.expenses / 1000).toFixed(1)}K` : Math.round(r.expenses)}
                  </div>
                  <div
                    className="w-7 md:w-10 bg-[#FF6B5C] rounded-t-md transition-all"
                    style={{ height: `${Math.max(expH, 2)}px` }}
                    title={`Ausgaben: ${formatEUR(r.expenses)}`}
                  />
                </div>
              </div>
              <div className="text-xs text-[#5B6371] uppercase tracking-wider mt-2 font-medium">{labelFor(r.month).split(" ")[0]}</div>
              <div className={`text-[11px] font-display font-semibold mt-0.5 ${r.net >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}>
                {r.net >= 0 ? "+" : ""}{formatEUR(r.net)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function CashflowSimulator({ forecast, currentYm, settings }) {
  // Liste verfügbarer Monate (sortiert)
  const months = useMemo(() => {
    const set = new Set(forecast.map((r) => r.month));
    // sicherstellen, dass aktueller Monat + nächste 15 Monate dabei sind
    const [y, m] = currentYm.split("-").map(Number);
    for (let i = 0; i < 15; i++) {
      const mm = m + i;
      const yy = y + Math.floor((mm - 1) / 12);
      const month = ((mm - 1) % 12) + 1;
      set.add(`${yy}-${String(month).padStart(2, "0")}`);
    }
    return Array.from(set).sort();
  }, [forecast, currentYm]);

  // Default-Zeitraum: aktueller Monat + nächste 2 Monate (also 3 Monate)
  const defaultEnd = months[Math.min(months.indexOf(currentYm) + 2, months.length - 1)] || currentYm;
  const [from, setFrom] = useState(currentYm);
  const [to, setTo] = useState(defaultEnd);
  const [mode, setMode] = useState("conservative");
  // Startguthaben: Fallback aus Settings, aber lokal überschreibbar für Was-wäre-wenn
  const [startBalance, setStartBalance] = useState(parseFloat(settings?.starting_balance ?? 0) || 0);
  useEffect(() => {
    setStartBalance(parseFloat(settings?.starting_balance ?? 0) || 0);
  }, [settings?.starting_balance]);

  // Preset-Zeiträume ab aktuellem Monat
  const setPreset = (n) => {
    setFrom(currentYm);
    const idx = months.indexOf(currentYm);
    const target = months[Math.min(idx + n - 1, months.length - 1)];
    if (target) setTo(target);
  };

  const fIdx = months.indexOf(from);
  const tIdx = months.indexOf(to);
  const rangeMonths = (fIdx >= 0 && tIdx >= fIdx) ? months.slice(fIdx, tIdx + 1) : [];

  // Daten aus forecast aggregieren
  const detail = useMemo(() => {
    const fmap = Object.fromEntries(forecast.map((r) => [r.month, r]));
    let income = 0, expenses = 0, overdueInc = 0;
    let running = startBalance;   // Kumulativer Kontostand startend vom Startguthaben
    let minBalance = running;
    let minBalanceMonth = from;
    const rows = rangeMonths.map((m) => {
      const r = fmap[m] || {};
      const actual = r.actual || 0;
      const openExpected = r.open_expected || 0;
      const expectedRolled = r.expected || 0;
      let incomeMonth;
      if (m < currentYm) {
        // Vergangenheit: nur wirklich eingegangene IST
        incomeMonth = actual;
      } else if (mode === "conservative") {
        // Konservativ: nur naturally fällige (open_expected) + IST
        incomeMonth = Math.max(openExpected, actual);
      } else {
        // Optimistisch: expected inkl. rollierte Überfällige
        incomeMonth = Math.max(expectedRolled, actual);
      }
      // Sammle wie viel davon "überfällig gerollt" ist (nur im aktuellen Monat)
      if (m === currentYm) {
        const rolled = Math.max(0, expectedRolled - openExpected);
        overdueInc += rolled;
      }
      // "Davon IST" & "noch offen" für die Transparenz-Zeile
      const incomeIst = actual;
      const incomeStillExpected = Math.max(0, incomeMonth - actual);
      const expensesMonth = r.expenses || 0;
      const expensesRecurring = r.expenses_recurring || 0;
      const expensesOnetime = r.expenses_onetime || 0;
      const net = incomeMonth - expensesMonth;
      running = running + net;
      if (running < minBalance) {
        minBalance = running;
        minBalanceMonth = m;
      }
      income += incomeMonth;
      expenses += expensesMonth;
      return {
        month: m,
        income: incomeMonth,
        income_ist: incomeIst,
        income_still_expected: incomeStillExpected,
        expenses: expensesMonth,
        expenses_recurring: expensesRecurring,
        expenses_onetime: expensesOnetime,
        net,
        balance: running,
        is_past: m < currentYm,
        is_current: m === currentYm,
      };
    });
    return { income, expenses, net: income - expenses, rows, overdueInc, endBalance: running, minBalance, minBalanceMonth };
  }, [forecast, rangeMonths, currentYm, mode, startBalance, from]);

  // Ampel-Status — Schwellen aus Settings (mit Fallback)
  const status = (() => {
    const green = parseFloat(settings?.cashflow_green_threshold ?? 5000);
    const yellow = parseFloat(settings?.cashflow_yellow_threshold ?? 0);
    const orange = parseFloat(settings?.cashflow_orange_threshold ?? -3000);
    if (detail.net >= green) return { color: "green", label: "Komfortable Lage", icon: "✓", desc: `Deutlicher Überschuss (≥ ${formatEUR(green)}) — du kannst Rücklagen aufbauen oder investieren.` };
    if (detail.net >= yellow) return { color: "yellow", label: "Vorsicht", icon: "!", desc: `Knapper Überschuss (${formatEUR(yellow)} – ${formatEUR(green)}) — Reserve dünn. Auf überfällige Forderungen achten.` };
    if (detail.net >= orange) return { color: "orange", label: "Achtung — eng", icon: "!!", desc: `Defizit klein (${formatEUR(orange)} – ${formatEUR(yellow)}), aber vorhanden. Aktiv mahnen oder Kosten reduzieren.` };
    return { color: "red", label: "Liquiditätsrisiko", icon: "⚠", desc: `Deutliches Defizit (unter ${formatEUR(orange)}). Sofort handeln: Forderungen einfordern, Ausgaben verschieben, ggf. Kreditlinie nutzen.` };
  })();

  const statusStyles = {
    green:  { bg: "#E6F7F7", border: "#0AA09E", text: "#0AA09E", iconBg: "#0AA09E" },
    yellow: { bg: "#FFF8E1", border: "#F59E0B", text: "#B45309", iconBg: "#F59E0B" },
    orange: { bg: "#FFF1EE", border: "#FF8C5C", text: "#C2410C", iconBg: "#FF8C5C" },
    red:    { bg: "#FFE4E0", border: "#FF6B5C", text: "#B91C1C", iconBg: "#FF6B5C" },
  }[status.color];

  const fmtMonth = (ym) => {
    const [y, mo] = ym.split("-");
    const names = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
    return `${names[parseInt(mo, 10) - 1]} ${y}`;
  };

  return (
    <section data-testid="cashflow-simulator" className="bg-white border border-[#E5E7EB] rounded-lg p-5 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow">Cashflow-Simulator</div>
          <h2 className="font-display text-2xl font-bold mt-1">Zeitraum prüfen</h2>
          <p className="text-xs text-[#5B6371] mt-1">
            Wähle einen Zeitraum und sieh sofort, ob du im Plus oder Minus bist — damit du nie zahlungsunfähig wirst.
            <br />
            <strong>Konservativ</strong> = nur diesen Monat naturally fällige Rechnungen · <strong>Optimistisch</strong> = plus überfällige aus Vormonaten (Roll-Forward).
          </p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Modus</label>
            <div className="inline-flex bg-[#F1F5F9] rounded-md p-1 text-xs font-medium h-[38px]">
              <button
                type="button"
                onClick={() => setMode("conservative")}
                className={`px-3 rounded ${mode === "conservative" ? "bg-white text-[#1F242E] shadow-sm" : "text-[#5B6371]"}`}
                data-testid="cashflow-sim-mode-conservative"
                title="Nur Rechnungen die in dem Monat naturally fällig werden (ohne überfällige aus Vormonaten)"
              >
                Konservativ
              </button>
              <button
                type="button"
                onClick={() => setMode("optimistic")}
                className={`px-3 rounded ${mode === "optimistic" ? "bg-white text-[#1F242E] shadow-sm" : "text-[#5B6371]"}`}
                data-testid="cashflow-sim-mode-optimistic"
                title="Naturally fällige + überfällige aus Vormonaten (wenn du davon ausgehst dass alle offenen Rechnungen zeitnah bezahlt werden)"
              >
                Optimistisch (mit Roll)
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Von</label>
            <select className="field !py-2 !text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="cashflow-sim-from">
              {months.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Bis</label>
            <select className="field !py-2 !text-sm" value={to} onChange={(e) => setTo(e.target.value)} data-testid="cashflow-sim-to">
              {months.map((m) => <option key={m} value={m} disabled={months.indexOf(m) < fIdx}>{fmtMonth(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Quick</label>
            <div className="inline-flex gap-1 h-[38px] items-center">
              {[3, 6, 9, 12].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPreset(n)}
                  className="text-xs px-2.5 py-1.5 rounded-md border border-[#E5E7EB] bg-white hover:border-[#0AA09E] hover:text-[#0AA09E]"
                  data-testid={`cashflow-sim-preset-${n}`}
                  title={`Ab jetzt ${n} Monate`}
                >
                  {n}M
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const resp = await api.get(`/docs/liquiditaet-generate?from_month=${from}&to_month=${to}`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `We_finance_Liquiditaetsnachweis_${from}_${to}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                alert("PDF-Erstellung fehlgeschlagen");
              }
            }}
            data-testid="cashflow-sim-pdf"
            className="bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2 h-[38px]"
            title="Liquiditätsnachweis als PDF für den gewählten Zeitraum herunterladen">
            📄 PDF erstellen
          </button>
        </div>
      </div>

      {/* Status-Ampel-Karte */}
      <div className="rounded-lg p-5 mb-4 border-2"
        style={{ backgroundColor: statusStyles.bg, borderColor: statusStyles.border }}
        data-testid={`cashflow-sim-status-${status.color}`}>
        <div className="flex items-start gap-4">
          <div className="rounded-full w-14 h-14 flex items-center justify-center text-white text-2xl font-bold shrink-0"
            style={{ backgroundColor: statusStyles.iconBg }}>
            {status.icon}
          </div>
          <div className="flex-1">
            <div className="font-display text-xl font-bold" style={{ color: statusStyles.text }}>{status.label}</div>
            <div className="text-sm mt-1" style={{ color: statusStyles.text }}>{status.desc}</div>
            {mode === "optimistic" && detail.overdueInc > 0 && rangeMonths.includes(currentYm) && (
              <div className="text-xs mt-2 bg-white/60 border border-current/20 rounded px-2 py-1.5 inline-block" style={{ color: statusStyles.text }} data-testid="cashflow-sim-overdue-warning">
                ⚠ Enthält <strong>{formatEUR(detail.overdueInc)}</strong> überfällige Rechnungen aus Vormonaten — dieses Geld ist noch nicht sicher da!
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: statusStyles.text }}>Saldo {rangeMonths.length} Monat{rangeMonths.length === 1 ? "" : "e"}</div>
            <div className="font-display text-3xl md:text-4xl font-bold mt-1" style={{ color: statusStyles.text }} data-testid="cashflow-sim-net">
              {detail.net >= 0 ? "+" : ""}{formatEUR(detail.net)}
            </div>
            <div className="text-[10px] uppercase tracking-widest mt-2" style={{ color: statusStyles.text }}>Kontostand Ende</div>
            <div className="font-display text-xl font-bold" style={{ color: statusStyles.text }} data-testid="cashflow-sim-end-balance">
              {formatEUR(detail.endBalance)}
            </div>
          </div>
        </div>
      </div>

      {/* Startguthaben + Mindest-Stand Warnung */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-4">
          <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Startguthaben (aktueller Kontostand)</label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              step="0.01"
              value={startBalance}
              onChange={(e) => setStartBalance(parseFloat(e.target.value) || 0)}
              className="field !py-2 !text-base font-display font-bold w-full"
              placeholder="0,00"
              data-testid="cashflow-sim-start-balance"
            />
            <span className="text-xs text-[#5B6371]">€</span>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-1">
            Trag deinen aktuellen Bankkontostand ein. Setze den Wert in den Einstellungen dauerhaft.
          </p>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Niedrigster Kontostand im Zeitraum</div>
          <div className={`font-display text-2xl font-bold mt-1 ${detail.minBalance >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}
            data-testid="cashflow-sim-min-balance">
            {formatEUR(detail.minBalance)}
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-1">
            {detail.minBalance < 0
              ? `⚠ Negativ in ${fmtMonth(detail.minBalanceMonth)} — Liquiditätsengpass droht`
              : `am Ende ${fmtMonth(detail.minBalanceMonth)}`}
          </p>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Ø pro Monat (Saldo)</div>
          <div className={`font-display text-2xl font-bold mt-1 ${detail.net >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}
            data-testid="cashflow-sim-avg">
            {rangeMonths.length > 0 ? formatEUR(detail.net / rangeMonths.length) : "—"}
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-1">
            Σ Einnahmen {formatEUR(detail.income)} · Σ Ausgaben {formatEUR(detail.expenses)}
          </p>
        </div>
      </div>

      {/* Detail-Tabelle pro Monat */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#5B6371] border-b border-[#E5E7EB]">
              <th className="text-left py-2 px-3 font-medium">Monat</th>
              <th className="text-right py-2 px-3 font-medium">Einnahmen</th>
              <th className="text-right py-2 px-3 font-medium">Ausgaben</th>
              <th className="text-right py-2 px-3 font-medium">Saldo</th>
              <th className="text-right py-2 px-3 font-medium">Kontostand Ende</th>
              <th className="text-center py-2 px-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {detail.rows.map((r) => {
              const dot = r.balance > 5000 ? "🟢" : r.balance > 0 ? "🟡" : r.balance > -3000 ? "🟠" : "🔴";
              return (
                <tr key={r.month} className={`border-b border-[#F1F5F9] ${r.is_past ? "bg-[#FAFBFC]" : ""}`} data-testid={`cashflow-sim-row-${r.month}`}>
                  <td className="py-2 px-3 font-medium">
                    {fmtMonth(r.month)}
                    {r.is_past && <span className="ml-1.5 text-[9px] px-1 py-0.5 bg-[#E5E7EB] text-[#5B6371] rounded">IST</span>}
                    {r.is_current && <span className="ml-1.5 text-[9px] px-1 py-0.5 bg-[#0AA09E] text-white rounded">AKTUELL</span>}
                  </td>
                  <td className="py-2 px-3 text-right text-[#0AA09E]">
                    <div className="font-medium">{formatEUR(r.income)}</div>
                    {!r.is_past && r.income_ist > 0 && (
                      <div className="text-[9px] text-[#94A3B8]">
                        davon IST: {formatEUR(r.income_ist)}
                        {r.income_still_expected > 0 && ` · offen: ${formatEUR(r.income_still_expected)}`}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right text-[#FF6B5C]">
                    <div className="font-medium">{formatEUR(r.expenses)}</div>
                    {r.expenses_onetime > 0 && (
                      <div className="text-[9px] text-[#94A3B8]">
                        davon einmalig: {formatEUR(r.expenses_onetime)}
                      </div>
                    )}
                  </td>
                  <td className={`py-2 px-3 text-right font-display font-bold ${r.net >= 0 ? "text-[#1F242E]" : "text-[#FF6B5C]"}`}>
                    {r.net >= 0 ? "+" : ""}{formatEUR(r.net)}
                  </td>
                  <td className={`py-2 px-3 text-right font-display font-bold ${r.balance >= 0 ? "text-[#1F242E]" : "text-[#FF6B5C]"}`}
                    data-testid={`cashflow-sim-balance-${r.month}`}>
                    {formatEUR(r.balance)}
                  </td>
                  <td className="py-2 px-3 text-center text-base">{dot}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-[#F8FAFC] border-t-2 border-[#0AA09E] font-display font-bold text-sm">
              <td className="py-2 px-3">Summe ({rangeMonths.length} Monate)</td>
              <td className="py-2 px-3 text-right text-[#0AA09E]">{formatEUR(detail.income)}</td>
              <td className="py-2 px-3 text-right text-[#FF6B5C]">{formatEUR(detail.expenses)}</td>
              <td className={`py-2 px-3 text-right ${detail.net >= 0 ? "text-[#1F242E]" : "text-[#FF6B5C]"}`}>
                {detail.net >= 0 ? "+" : ""}{formatEUR(detail.net)}
              </td>
              <td className={`py-2 px-3 text-right ${detail.endBalance >= 0 ? "text-[#1F242E]" : "text-[#FF6B5C]"}`}>
                {formatEUR(detail.endBalance)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}


// ------------------------------------------------------------------
// Deckungsbeitrag pro Maßnahme
// ------------------------------------------------------------------
function ContributionMarginWidget() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("total"); // "total" | "monthly"
  const [monthFilter, setMonthFilter] = useState(""); // YYYY-MM (only in monthly mode)
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/contribution-margin");
        if (alive) setPayload(data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const measures = payload?.measures || [];
  const overall = payload?.overall || {};

  // Sammle alle vorkommenden Monate für Filter (monthly mode)
  const allMonths = useMemo(() => {
    const s = new Set();
    for (const m of measures) {
      for (const pm of (m.per_month || [])) s.add(pm.month);
    }
    return Array.from(s).sort();
  }, [measures]);

  // Set default month once when switching to monthly
  useEffect(() => {
    if (mode === "monthly" && !monthFilter && allMonths.length > 0) {
      // Bevorzugt aktueller Monat, sonst erster
      const now = new Date();
      const curYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      setMonthFilter(allMonths.includes(curYm) ? curYm : allMonths[0]);
    }
  }, [mode, monthFilter, allMonths]);

  // Für monatlichen Modus: Zeilen aus per_month projizieren
  const rows = useMemo(() => {
    if (mode === "total") {
      return measures.map((m) => ({
        id: m.id,
        type: m.type,
        customer_name: m.customer_name,
        measure_type: m.measure_type,
        revenue: m.revenue,
        direct: m.direct_total,
        overhead: m.overhead_allocated,
        cm_gross: m.cm_gross,
        cm_gross_pct: m.cm_gross_pct,
        cm_net: m.cm_net,
        cm_net_pct: m.cm_net_pct,
      }));
    }
    // monthly
    const out = [];
    for (const m of measures) {
      const pm = (m.per_month || []).find((r) => r.month === monthFilter);
      if (!pm) continue;
      out.push({
        id: m.id + "-" + monthFilter,
        type: m.type,
        customer_name: m.customer_name,
        measure_type: m.measure_type,
        revenue: pm.revenue,
        direct: pm.direct,
        overhead: pm.overhead,
        cm_gross: pm.revenue - pm.direct,
        cm_gross_pct: pm.revenue > 0 ? ((pm.revenue - pm.direct) / pm.revenue * 100) : 0,
        cm_net: pm.cm_net,
        cm_net_pct: pm.cm_net_pct,
      });
    }
    return out;
  }, [measures, mode, monthFilter]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => b.cm_net - a.cm_net);
  }, [rows]);

  const shown = sortedRows.slice(0, limit);
  const flops = [...sortedRows].reverse().slice(0, 3);

  const monthlyTotal = useMemo(() => {
    if (mode !== "monthly") return null;
    return rows.reduce((a, r) => a + r.cm_net, 0);
  }, [rows, mode]);

  if (loading) {
    return (
      <section data-testid="contribution-margin-widget">
        <div className="text-sm text-[#5B6371]">Lade Deckungsbeiträge …</div>
      </section>
    );
  }

  return (
    <section data-testid="contribution-margin-widget">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <ChartPieSlice size={14} weight="duotone" /> Deckungsbeitrag pro Maßnahme
          </div>
          <h2 className="font-display text-2xl font-bold mt-1">Umsatz − direkte Kosten − Gemeinkosten</h2>
          <p className="text-xs text-[#5B6371] mt-1">
            Gemeinkosten (Fixausgaben) anteilig nach Umsatz verteilt. Direkte Kosten pro Maßnahme im Formular pflegen (Personal €/UE, Material, Raum).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-[#F1F5F9] rounded-md p-1 text-xs font-medium">
            <button
              className={`px-3 py-1.5 rounded ${mode === "total" ? "bg-white text-[#1F242E] shadow-sm" : "text-[#5B6371]"}`}
              onClick={() => setMode("total")}
              data-testid="cm-mode-total"
            >
              Gesamt
            </button>
            <button
              className={`px-3 py-1.5 rounded ${mode === "monthly" ? "bg-white text-[#1F242E] shadow-sm" : "text-[#5B6371]"}`}
              onClick={() => setMode("monthly")}
              data-testid="cm-mode-monthly"
            >
              Monatlich
            </button>
          </div>
          {mode === "monthly" && (
            <select
              className="field text-xs py-1.5"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              data-testid="cm-month-select"
            >
              {allMonths.map((m) => (
                <option key={m} value={m}>{formatMonth(m)}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Overall KPIs */}
      {mode === "total" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <MiniKpi label="Umsatz gesamt" value={formatEUR(overall.total_revenue || 0)} testId="cm-kpi-revenue" />
          <MiniKpi label="Direkte Kosten" value={formatEUR(overall.total_direct || 0)} testId="cm-kpi-direct" />
          <MiniKpi label="Gemeinkosten (verteilt)" value={formatEUR(overall.total_overhead_allocated || 0)} testId="cm-kpi-overhead" />
          <MiniKpi
            label="Deckungsbeitrag netto"
            value={formatEUR(overall.total_cm_net || 0)}
            testId="cm-kpi-net"
            highlight={(overall.total_cm_net || 0) >= 0 ? "pos" : "neg"}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <MiniKpi label={`DB netto ${monthFilter ? formatMonth(monthFilter) : ""}`}
            value={formatEUR(monthlyTotal || 0)}
            highlight={(monthlyTotal || 0) >= 0 ? "pos" : "neg"}
            testId="cm-kpi-monthly-net" />
          <MiniKpi label="Aktive Maßnahmen im Monat" value={String(rows.length)} testId="cm-kpi-monthly-count" />
          <MiniKpi label="Umsatz im Monat"
            value={formatEUR(rows.reduce((a, r) => a + r.revenue, 0))}
            testId="cm-kpi-monthly-revenue" />
        </div>
      )}

      {/* Table (Top ranking) */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden mb-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cm-table">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
                <th className="text-left px-3 py-3 font-medium">#</th>
                <th className="text-left px-3 py-3 font-medium">Typ</th>
                <th className="text-left px-3 py-3 font-medium">Teilnehmer / Kurs</th>
                <th className="text-right px-3 py-3 font-medium">Umsatz</th>
                <th className="text-right px-3 py-3 font-medium">Direkt</th>
                <th className="text-right px-3 py-3 font-medium">Gemein</th>
                <th className="text-right px-3 py-3 font-medium">DB netto</th>
                <th className="text-right px-3 py-3 font-medium">DB %</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-[#94A3B8]">
                    Keine Maßnahmen{mode === "monthly" ? " in diesem Monat" : ""}.
                  </td>
                </tr>
              )}
              {shown.map((r, idx) => (
                <tr key={r.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2.5 text-[#94A3B8]">{idx + 1}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      r.type === "AVGS" ? "bg-[#E6F7F7] text-[#0AA09E]" : "bg-[#FFF1EE] text-[#FF6B5C]"
                    }`}>{r.type}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-[#1F242E]">{r.customer_name}</div>
                    {r.measure_type && <div className="text-[10px] text-[#94A3B8]">{r.measure_type}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-right">{formatEUR(r.revenue)}</td>
                  <td className="px-3 py-2.5 text-right text-[#5B6371]">{formatEUR(r.direct)}</td>
                  <td className="px-3 py-2.5 text-right text-[#5B6371]">{formatEUR(r.overhead)}</td>
                  <td className={`px-3 py-2.5 text-right font-display font-bold ${r.cm_net >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}>
                    {formatEUR(r.cm_net)}
                  </td>
                  <td className={`px-3 py-2.5 text-right ${r.cm_net_pct >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}>
                    {r.cm_net_pct.toFixed(1)} %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sortedRows.length > limit && (
          <div className="text-center py-2 border-t border-[#F1F5F9]">
            <button className="text-xs text-[#0AA09E] hover:underline"
              onClick={() => setLimit(limit + 10)} data-testid="cm-show-more">
              Weitere {Math.min(10, sortedRows.length - limit)} anzeigen …
            </button>
          </div>
        )}
      </div>

      {/* Flop 3 — negative Kandidaten */}
      {flops.length > 0 && flops.some((r) => r.cm_net < 0) && (
        <div className="bg-[#FFF1EE] border border-[#FFD9D2] rounded-lg p-4" data-testid="cm-flops">
          <div className="label-eyebrow text-[#FF6B5C] mb-2">Achtung: Verlustbringer (Top 3)</div>
          <div className="space-y-1 text-sm">
            {flops.filter((r) => r.cm_net < 0).map((r) => (
              <div key={r.id} className="flex items-center justify-between">
                <span>{r.type} · {r.customer_name} {r.measure_type ? `(${r.measure_type})` : ""}</span>
                <span className="font-bold text-[#FF6B5C]">{formatEUR(r.cm_net)} ({r.cm_net_pct.toFixed(1)} %)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function MiniKpi({ label, value, sub, testId, highlight }) {
  const colorClass = highlight === "pos" ? "text-[#0AA09E]" : highlight === "neg" ? "text-[#FF6B5C]" : "text-[#1F242E]";
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">{label}</div>
      <div className={`font-display text-xl font-semibold mt-1 ${colorClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#94A3B8] mt-0.5">{sub}</div>}
    </div>
  );
}


// ------------------------------------------------------------------
// Break-even UE Rechner: Wie viele UEs muss ich pro Monat verkaufen?
// ------------------------------------------------------------------
function BreakEvenUeCalculator({ data }) {
  const settings = data?.settings || {};
  const cm = data?.current_month || {};
  const forecast = data?.forecast || [];

  // Break-even = echte Ausgaben dieses Monats aus der Fixkosten-Liste (Einstellung nur als Fallback)
  const breakEven = parseFloat(cm.break_even || settings.break_even_monthly || 0);
  const beFromExpenses = cm.break_even_source === "expenses";
  const revenueTarget = parseFloat(settings.revenue_target_monthly || cm.revenue_target_monthly || 0);
  const profitTarget = parseFloat(settings.profit_target_monthly || cm.profit_target_monthly || 0);
  const pricePerUeDefault = parseFloat(settings.price_per_ue || 57.87);

  const [pricePerUe, setPricePerUe] = useState(pricePerUeDefault);
  const [directCostPerUe, setDirectCostPerUe] = useState(0);
  useEffect(() => { setPricePerUe(pricePerUeDefault); }, [pricePerUeDefault]);

  const cmPerUe = Math.max(0.01, pricePerUe - directCostPerUe);
  const ueBreakEven = breakEven > 0 ? Math.ceil(breakEven / cmPerUe) : 0;
  const ueTarget = revenueTarget > 0 ? Math.ceil(revenueTarget / cmPerUe) : 0;
  const ueProfit = profitTarget > 0 ? Math.ceil((breakEven + profitTarget) / cmPerUe) : 0;

  // Cash-Sicht vergleicht nur Rechnungen, die DIESEN Monat fällig sind (ohne Überfällige aus Vormonaten)
  const expectedThisMonth = parseFloat(cm.expected_this_month_pure || 0);
  const overdueRolledMoney = parseFloat(cm.overdue_rolled_into_month || 0);

  // UEs gestartet und eingehend im aktuellen Monat — direkt vom Backend (keine €÷Preis-Schätzung)
  const currentRow = forecast.find((r) => r.month === cm.ym) || {};
  const uesStartedThisMonth = parseFloat(currentRow.ue_started || 0);
  const uesStartedCountThisMonth = parseInt(currentRow.ue_started_count || 0);
  const uesIncomingPure = parseFloat(currentRow.ue_incoming_pure || 0);   // nur diesen Monat naturally fällig
  const uesIncomingRolled = parseFloat(currentRow.ue_incoming || 0);      // inkl. überfällige aus Vormonaten
  // Wir verwenden die REINE UE-Zahl (nicht rolliert) für Break-even-Vergleich
  const uesSoldEstimate = Math.round(uesIncomingPure);

  const gapMoney = Math.max(0, breakEven - expectedThisMonth);
  const gapUe = Math.ceil(gapMoney / cmPerUe);
  const beReached = expectedThisMonth >= breakEven;
  const gapMoneyProfit = Math.max(0, (breakEven + profitTarget) - expectedThisMonth);
  const gapUeProfit = Math.ceil(gapMoneyProfit / cmPerUe);
  const profitReached = expectedThisMonth >= breakEven + profitTarget;

  // ZUSÄTZLICH: Break-even aus Verkaufs-Sicht (ue_started pro Monat).
  // Verkaufte UEs werden erst in Folgemonaten zu Cash — zeigt ob der Verkaufsmotor läuft.
  const uesStartedRound = Math.round(uesStartedThisMonth);
  const beSalesReached = uesStartedRound >= ueBreakEven;
  const gapUeSales = Math.max(0, ueBreakEven - uesStartedRound);
  const profitSalesReached = uesStartedRound >= ueProfit;
  const gapUeSalesProfit = Math.max(0, ueProfit - uesStartedRound);

  // Nächste 6 Monate ab aktuellem (inkl.)
  const next6 = useMemo(() => {
    const sorted = [...forecast].sort((a, b) => a.month.localeCompare(b.month));
    const idx = sorted.findIndex((r) => r.month === cm.ym);
    const start = idx >= 0 ? idx : 0;
    return sorted.slice(start, start + 6);
  }, [forecast, cm.ym]);

  const past3 = useMemo(() => {
    const sorted = [...forecast].sort((a, b) => a.month.localeCompare(b.month));
    const idx = sorted.findIndex((r) => r.month === cm.ym);
    const start = Math.max(0, (idx >= 0 ? idx : sorted.length) - 3);
    const end = idx >= 0 ? idx : sorted.length;
    return sorted.slice(start, end);
  }, [forecast, cm.ym]);

  const avgPast3 = past3.length > 0 ? past3.reduce((a, r) => a + (r.actual || 0), 0) / past3.length : 0;
  const uesAvgPast3 = pricePerUe > 0 ? Math.round(avgPast3 / pricePerUe) : 0;
  const trendGap = ueBreakEven - uesAvgPast3;

  return (
    <section data-testid="break-even-ue-widget">
      <div className="flex flex-wrap items-end justify-between mb-3 gap-2">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Target size={14} weight="duotone" /> Break-even-Rechner
          </div>
          <h2 className="font-display text-2xl font-bold mt-1">Wie viele UE musst du verkaufen?</h2>
          <p className="text-xs text-[#5B6371] mt-1">
            Monatliche UE-Ziele für Break-even, Umsatzziel und Gewinnziel. Preis und direkte Kosten pro UE anpassen um Szenarien zu prüfen.
          </p>
        </div>
      </div>

      {/* Eingabe-Zeile */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371] block mb-1">Preis pro UE (€)</label>
            <input
              type="number" step="0.01" min="0"
              className="field w-full"
              value={pricePerUe}
              onChange={(e) => setPricePerUe(parseFloat(e.target.value || 0))}
              data-testid="be-price-per-ue"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371] block mb-1">Direkte Kosten pro UE (€)</label>
            <input
              type="number" step="0.01" min="0"
              className="field w-full"
              value={directCostPerUe}
              onChange={(e) => setDirectCostPerUe(parseFloat(e.target.value || 0))}
              placeholder="z.B. Dozentenhonorar"
              data-testid="be-direct-cost-per-ue"
            />
          </div>
          <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-2.5">
            <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Deckungsbeitrag / UE</div>
            <div className="font-display text-xl font-bold text-[#0AA09E]" data-testid="be-cm-per-ue">
              {formatEUR(cmPerUe)}
            </div>
            <div className="text-[10px] text-[#94A3B8]">= {formatEUR(pricePerUe)} − {formatEUR(directCostPerUe)}</div>
          </div>
        </div>
      </div>

      {/* Ziel-Karten */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="bg-white border-2 border-[#FF6B5C] rounded-lg p-4" data-testid="be-card-breakeven">
          <div className="flex items-center justify-between mb-1">
            <div className="label-eyebrow text-[#FF6B5C]">Break-even</div>
            <span className="text-[10px] text-[#94A3B8]">Fixausgaben decken</span>
          </div>
          <div className="font-display text-3xl font-bold text-[#FF6B5C] mt-2" data-testid="be-ue-breakeven">
            {ueBreakEven} UE
          </div>
          <div className="text-xs text-[#5B6371] mt-1">
            pro Monat, um <strong>{formatEUR(breakEven)}</strong> {beFromExpenses ? "Ausgaben (laut Ausgaben-Liste)" : "Fixkosten"} zu decken
          </div>
          <div className="text-[11px] text-[#94A3B8] mt-2 pt-2 border-t border-[#F1F5F9]">
            = {formatEUR(breakEven)} ÷ {formatEUR(cmPerUe)}/UE
          </div>
        </div>

        <div className="bg-white border-2 border-[#F59E0B] rounded-lg p-4" data-testid="be-card-target">
          <div className="flex items-center justify-between mb-1">
            <div className="label-eyebrow" style={{ color: "#F59E0B" }}>Umsatzziel</div>
            <span className="text-[10px] text-[#94A3B8]">Solltrend</span>
          </div>
          <div className="font-display text-3xl font-bold mt-2" style={{ color: "#F59E0B" }} data-testid="be-ue-target">
            {ueTarget} UE
          </div>
          <div className="text-xs text-[#5B6371] mt-1">
            pro Monat für <strong>{formatEUR(revenueTarget)}</strong> Umsatzziel
          </div>
          <div className="text-[11px] text-[#94A3B8] mt-2 pt-2 border-t border-[#F1F5F9]">
            = {formatEUR(revenueTarget)} ÷ {formatEUR(cmPerUe)}/UE
          </div>
        </div>

        <div className="bg-white border-2 border-[#0AA09E] rounded-lg p-4" data-testid="be-card-profit">
          <div className="flex items-center justify-between mb-1">
            <div className="label-eyebrow text-[#0AA09E]">Gewinnziel</div>
            <span className="text-[10px] text-[#94A3B8]">+ Gewinn</span>
          </div>
          <div className="font-display text-3xl font-bold text-[#0AA09E] mt-2" data-testid="be-ue-profit">
            {ueProfit} UE
          </div>
          <div className="text-xs text-[#5B6371] mt-1">
            pro Monat für <strong>{formatEUR(profitTarget)}</strong> Gewinn oben drauf
          </div>
          <div className="text-[11px] text-[#94A3B8] mt-2 pt-2 border-t border-[#F1F5F9]">
            = ({formatEUR(breakEven)} + {formatEUR(profitTarget)}) ÷ {formatEUR(cmPerUe)}/UE
          </div>
        </div>
      </div>

      {/* Erklärbox: zwei Sichten */}
      <div className="rounded-lg p-3 bg-[#F8FAFC] border border-[#E5E7EB] mb-3 text-xs text-[#5B6371]" data-testid="be-explain">
        <strong className="text-[#1F242E]">Zwei Sichten auf UE-Ziele:</strong>
        <ul className="mt-1 space-y-0.5 list-disc pl-5">
          <li><strong className="text-[#0AA09E]">💰 Cash-Sicht</strong> = UEs, deren Rechnung diesen Monat <em>bezahlt werden sollte</em> (meist aus Kursen die vor 1-3 Monaten geendet haben)</li>
          <li><strong className="text-[#0AA09E]">🚀 Verkaufs-Sicht</strong> = UEs die in Kursen stecken, die <em>diesen Monat starten</em> (Geld kommt erst 1-5 Monate später)</li>
        </ul>
      </div>

      {/* Zwei Break-even-Karten nebeneinander */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        {/* CASH-SICHT */}
        <div className={`rounded-lg p-4 border-2 ${beReached ? "bg-[#E6F7F7] border-[#0AA09E]" : "bg-[#FFF1EE] border-[#FF6B5C]"}`} data-testid="be-cash-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">💰 Cash-Sicht — {formatMonth(cm.ym)}</div>
              <div className="text-xs text-[#5B6371]">Rechnungen die diesen Monat bezahlt werden sollten</div>
            </div>
            <div className={`text-[10px] px-2 py-0.5 rounded ${beReached ? "bg-[#0AA09E] text-white" : "bg-[#FF6B5C] text-white"}`}>
              {beReached ? "✓ erreicht" : `fehlt: ${gapUe} UE`}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Erwartet eingehend</div>
              <div className="font-display text-2xl font-bold text-[#1F242E]" data-testid="be-current-ues">{uesSoldEstimate} UE</div>
              <div className="text-[10px] text-[#94A3B8]">= {formatEUR(expectedThisMonth)}</div>
              {overdueRolledMoney > 0 && (
                <div className="text-[10px] text-[#FF6B5C] mt-0.5" data-testid="be-rolled-note">
                  + {formatEUR(overdueRolledMoney)} ({Math.round(uesIncomingRolled - uesIncomingPure)} UE) überfällig aus Vormonaten — nicht eingerechnet
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Break-even Ziel</div>
              <div className="font-display text-2xl font-bold text-[#1F242E]" data-testid="be-cash-target-ue">{ueBreakEven} UE</div>
              <div className="text-[10px] text-[#94A3B8]" data-testid="be-cash-target-eur">
                = {formatEUR(breakEven)} {beFromExpenses ? "Ausgaben diesen Monat" : "Fixkosten (Einstellung)"}
              </div>
            </div>
          </div>
          <div className="mt-2">
            <div className="h-2 bg-white rounded-full overflow-hidden border border-[#E5E7EB]">
              <div className={`h-full rounded-full ${beReached ? "bg-[#0AA09E]" : "bg-[#FF6B5C]"}`}
                style={{ width: `${Math.min(100, breakEven > 0 ? (expectedThisMonth / breakEven * 100) : 0)}%` }} />
            </div>
          </div>
          <div className={`text-xs mt-2 font-medium ${beReached ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`} data-testid="be-cash-status">
            {beReached
              ? `✓ ${formatEUR(expectedThisMonth - breakEven)} über Break-even`
              : `⚠ ${gapUe} UE fehlen (${formatEUR(gapMoney)})`}
          </div>
        </div>

        {/* VERKAUFS-SICHT */}
        <div className={`rounded-lg p-4 border-2 ${beSalesReached ? "bg-[#E6F7F7] border-[#0AA09E]" : "bg-[#FFF8ED] border-[#F59E0B]"}`} data-testid="be-sales-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">🚀 Verkaufs-Sicht — {formatMonth(cm.ym)}</div>
              <div className="text-xs text-[#5B6371]">Neue Kurse diesen Monat gestartet (Geld folgt später)</div>
            </div>
            <div className={`text-[10px] px-2 py-0.5 rounded ${beSalesReached ? "bg-[#0AA09E] text-white" : "bg-[#F59E0B] text-white"}`}>
              {beSalesReached ? "✓ verkauft genug" : `fehlt: ${gapUeSales} UE`}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Verkauft (gestartet)</div>
              <div className="font-display text-2xl font-bold text-[#1F242E]" data-testid="be-ue-started">{uesStartedRound} UE</div>
              <div className="text-[10px] text-[#94A3B8]">{uesStartedCountThisMonth} neue Projekte</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Verkaufs-Ziel</div>
              <div className="font-display text-2xl font-bold text-[#1F242E]">{ueBreakEven} UE</div>
              <div className="text-[10px] text-[#94A3B8]">Gewinnziel: {ueProfit} UE</div>
            </div>
          </div>
          <div className="mt-2">
            <div className="h-2 bg-white rounded-full overflow-hidden border border-[#E5E7EB]">
              <div className={`h-full rounded-full ${beSalesReached ? "bg-[#0AA09E]" : "bg-[#F59E0B]"}`}
                style={{ width: `${Math.min(100, ueBreakEven > 0 ? (uesStartedRound / ueBreakEven * 100) : 0)}%` }} />
            </div>
          </div>
          <div className={`text-xs mt-2 font-medium ${beSalesReached ? "text-[#0AA09E]" : "text-[#F59E0B]"}`}>
            {beSalesReached
              ? profitSalesReached
                ? `✓ ${uesStartedRound - ueProfit} UE über Gewinnziel · Super Verkaufsmonat!`
                : `✓ ${uesStartedRound - ueBreakEven} UE über Break-even · noch ${gapUeSalesProfit} UE bis Gewinnziel`
              : `⚠ ${gapUeSales} UE Verkaufslücke`}
          </div>
        </div>
      </div>

      {/* Ist vs. Break-even mit Prognose */}
      <div className={`rounded-lg p-4 border-2 ${beReached ? "bg-[#E6F7F7] border-[#0AA09E]" : "bg-[#FFF1EE] border-[#FF6B5C]"} mb-3 hidden`} data-testid="be-status-card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* UEs eingehend als Geld */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">💰 Geld eingehend {formatMonth(cm.ym)}</div>
            <div className="font-display text-2xl font-bold text-[#1F242E]" data-testid="be-current-ues">
              {uesSoldEstimate} UE
            </div>
            <div className="text-[11px] text-[#94A3B8]">= {formatEUR(expectedThisMonth)} erwartet</div>
            {uesIncomingRolled > uesIncomingPure && (
              <div className="text-[10px] text-[#FF6B5C] mt-0.5" data-testid="be-rolled-note">
                davon {Math.round(uesIncomingRolled - uesIncomingPure)} UE überfällig aus Vormonaten
              </div>
            )}
          </div>
          {/* UEs gestartet in diesem Monat */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">🚀 Projekte gestartet {formatMonth(cm.ym)}</div>
            <div className="font-display text-2xl font-bold text-[#1F242E]" data-testid="be-ue-started">
              {Math.round(uesStartedThisMonth)} UE
            </div>
            <div className="text-[11px] text-[#94A3B8]">{uesStartedCountThisMonth} Projekte gestartet</div>
          </div>
          {/* Gap Break-even */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">
              🎯 Fehlt für Break-even
            </div>
            <div className={`font-display text-2xl font-bold ${beReached ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`} data-testid="be-gap-breakeven">
              {beReached ? `+${uesSoldEstimate - ueBreakEven} UE` : `${gapUe} UE`}
            </div>
            <div className="text-[11px] text-[#94A3B8]">
              {beReached ? "über Break-even" : `= ${formatEUR(gapMoney)}`}
            </div>
          </div>
          {/* Gap Gewinnziel */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">
              💎 Fehlt für Gewinnziel
            </div>
            <div className={`font-display text-2xl font-bold ${profitReached ? "text-[#0AA09E]" : "text-[#F59E0B]"}`} data-testid="be-gap-profit">
              {profitReached ? `+${uesSoldEstimate - ueProfit} UE` : `${gapUeProfit} UE`}
            </div>
            <div className="text-[11px] text-[#94A3B8]">
              {profitReached ? "über Gewinnziel" : `= ${formatEUR(gapMoneyProfit)}`}
            </div>
          </div>
        </div>
        <div className={`text-base font-display font-bold mt-3 ${beReached ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`} data-testid="be-status-text">
          {beReached
            ? `✓ Break-even erreicht (${uesSoldEstimate} von ${ueBreakEven} UE) · Ø letzte 3 Monate: ${uesAvgPast3} UE`
            : `⚠ ${gapUe} UE fehlen für Break-even · Ø letzte 3 Monate: ${uesAvgPast3} UE`}
        </div>
        <div className="mt-2">
          <div className="h-3 bg-white rounded-full overflow-hidden border border-[#E5E7EB]">
            <div
              className={`h-full rounded-full transition-all ${beReached ? "bg-[#0AA09E]" : "bg-[#FF6B5C]"}`}
              style={{ width: `${Math.min(100, ueBreakEven > 0 ? (uesSoldEstimate / ueBreakEven * 100) : 0)}%` }}
              data-testid="be-progress-bar"
            />
          </div>
          <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1">
            <span>0 UE</span>
            <span>Break-even: {ueBreakEven} UE · Gewinn: {ueProfit} UE</span>
          </div>
        </div>
      </div>

      {/* 6-Monats-Vorschau */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="be-6month-table">
        <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E5E7EB]">
          <div className="label-eyebrow">Nächste 6 Monate — UE-Prognose</div>
          <p className="text-[11px] text-[#5B6371] mt-1">
            Vergleich: gestartete Projekte (neue Maßnahmen) vs. eingehendes Geld (Zahlungen) vs. Break-even-Ziel
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b border-[#E5E7EB] bg-[#F8FAFC]">
                <th className="text-left px-4 py-2.5 font-medium">Monat</th>
                <th className="text-right px-4 py-2.5 font-medium">🚀 Gestartet</th>
                <th className="text-right px-4 py-2.5 font-medium">💰 Eingehend UE</th>
                <th className="text-right px-4 py-2.5 font-medium">💰 Eingehend €</th>
                <th className="text-right px-4 py-2.5 font-medium">🎯 Break-even Δ</th>
                <th className="text-right px-4 py-2.5 font-medium">💎 Gewinn Δ</th>
              </tr>
            </thead>
            <tbody>
              {next6.map((r) => {
                const inUe = Math.round(r.ue_incoming_pure || 0);
                const gapBE = ueBreakEven - inUe;
                const gapPR = ueProfit - inUe;
                const isCurrent = r.month === cm.ym;
                return (
                  <tr key={r.month} className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] ${isCurrent ? "bg-[#F5FBFB]" : ""}`}
                      data-testid={`be-row-${r.month}`}>
                    <td className="px-4 py-2.5 font-medium">
                      {formatMonth(r.month)}
                      {isCurrent && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[#0AA09E]">aktuell</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <strong>{Math.round(r.ue_started || 0)} UE</strong>
                      <div className="text-[10px] text-[#94A3B8]">{r.ue_started_count || 0} Projekte</div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-display font-bold text-[#1F242E]">{inUe} UE</td>
                    <td className="px-4 py-2.5 text-right text-[#0AA09E]">{formatEUR(r.open_expected || 0)}</td>
                    <td className={`px-4 py-2.5 text-right font-medium ${gapBE <= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}`}>
                      {gapBE <= 0 ? `+${Math.abs(gapBE)} UE` : `−${gapBE} UE`}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-medium ${gapPR <= 0 ? "text-[#0AA09E]" : "text-[#F59E0B]"}`}>
                      {gapPR <= 0 ? `+${Math.abs(gapPR)} UE` : `−${gapPR} UE`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}


// ------------------------------------------------------------------
// Fällige Rechnungen pro Monat — MIT vs. OHNE rollierte Vergangenheits-Rechnungen
// ------------------------------------------------------------------
function DuePerMonthWidget({ forecast, currentYm }) {
  const [mode, setMode] = useState("with"); // "with" | "without"
  // Zeige nur Monate mit offenen Beträgen (aktuell + Zukunft, plus Vergangenheit falls open_expected > 0)
  const rows = useMemo(() => {
    return (forecast || [])
      .filter((r) => (r.open_expected || 0) > 0 || (r.expected || 0) > 0)
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [forecast]);

  if (!rows.length) return null;

  // Kumulierte Überfällige (bis zum aktuellen Monat, exkl. current)
  const overdueBeforeCurrent = rows
    .filter((r) => r.month < currentYm)
    .reduce((s, r) => s + (r.open_expected || 0), 0);

  // Summen für Modus
  const sumWithRoll = rows.filter((r) => r.month >= currentYm).reduce((s, r) => s + (r.expected || 0), 0);
  const sumWithoutRoll = rows.reduce((s, r) => s + (r.open_expected || 0), 0);

  return (
    <section data-testid="due-per-month-widget">
      <div className="flex flex-wrap items-end justify-between mb-3 gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Clock size={14} weight="duotone" /> Fällige Rechnungen pro Monat
          </div>
          <h2 className="font-display text-2xl font-bold mt-1">
            {mode === "with" ? "Mit rollierten Vergangenheits-Rechnungen" : "Ohne rollierte Vergangenheits-Rechnungen"}
          </h2>
          <p className="text-xs text-[#5B6371] mt-1">
            Basis: Maßnahme-Ende + Zahlungsziel des Kunden. "Ohne" zeigt jede Rechnung in dem Monat wo sie <em>eigentlich</em> fällig war;
            "Mit" rollt überfällige Vormonats-Rechnungen in den aktuellen Monat.
          </p>
        </div>
        <div className="inline-flex bg-[#F1F5F9] rounded-md p-1 text-xs font-medium">
          <button
            type="button"
            onClick={() => setMode("with")}
            className={`px-3 py-1.5 rounded ${mode === "with" ? "bg-white text-[#1F242E] shadow-sm" : "text-[#5B6371]"}`}
            data-testid="due-mode-with"
          >
            Mit Vergangenheit (rollierend)
          </button>
          <button
            type="button"
            onClick={() => setMode("without")}
            className={`px-3 py-1.5 rounded ${mode === "without" ? "bg-white text-[#1F242E] shadow-sm" : "text-[#5B6371]"}`}
            data-testid="due-mode-without"
          >
            Ohne Vergangenheit (rein)
          </button>
        </div>
      </div>

      {/* Warnhinweis überfällige */}
      {overdueBeforeCurrent > 0 && (
        <div className="mb-3 rounded-lg p-3 bg-[#FFF1EE] border border-[#FFD9D2] text-sm text-[#1F242E]" data-testid="due-overdue-hint">
          <strong className="text-[#FF6B5C]">Achtung — {formatEUR(overdueBeforeCurrent)} überfällig aus Vormonaten.</strong>
          {mode === "with"
            ? " Diese Beträge sind in den Monats-Summen unten unter aktueller Monat zusammengefasst (Roll-Forward)."
            : " Diese Beträge werden unten in ihrem Original-Monat gezeigt — nicht im aktuellen Monat."}
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="due-table">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
                <th className="text-left px-4 py-3 font-medium">Monat</th>
                <th className="text-right px-4 py-3 font-medium">AVGS</th>
                <th className="text-right px-4 py-3 font-medium">FBW</th>
                <th className="text-right px-4 py-3 font-medium">Gesamt fällig</th>
                <th className="text-right px-4 py-3 font-medium">Rg.</th>
                <th className="text-right px-4 py-3 font-medium">Kumuliert</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let cum = 0;
                return rows.map((r) => {
                  const isPast = r.month < currentYm;
                  const val = mode === "with"
                    ? (isPast ? 0 : (r.expected || 0))
                    : (r.open_expected || 0);
                  const val_avgs = mode === "with"
                    ? (isPast ? 0 : (r.expected_avgs || 0))
                    : (r.open_expected_avgs || 0);
                  const val_fbw = mode === "with"
                    ? (isPast ? 0 : (r.expected_fbw || 0))
                    : (r.open_expected_fbw || 0);
                  const cnt = mode === "with"
                    ? (isPast ? 0 : (r.expected_count || 0))
                    : (r.open_expected_count || 0);
                  if (val === 0) return null;
                  cum += val;
                  const isCurrent = r.month === currentYm;
                  return (
                    <tr key={r.month} className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] ${isCurrent ? "bg-[#F5FBFB]" : ""}`}
                        data-testid={`due-row-${r.month}`}>
                      <td className="px-4 py-2.5 font-medium">
                        {formatMonth(r.month)}
                        {isPast && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[#FF6B5C]">überfällig</span>}
                        {isCurrent && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[#0AA09E]">aktuell</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right text-[#0AA09E]">{formatEUR(val_avgs)}</td>
                      <td className="px-4 py-2.5 text-right text-[#3FB5B0]">{formatEUR(val_fbw)}</td>
                      <td className="px-4 py-2.5 text-right font-display font-bold text-[#1F242E]">{formatEUR(val)}</td>
                      <td className="px-4 py-2.5 text-right text-xs text-[#5B6371]">{cnt}×</td>
                      <td className="px-4 py-2.5 text-right text-[#5B6371]">{formatEUR(cum)}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
            <tfoot>
              <tr className="bg-[#F8FAFC] border-t-2 border-[#E5E7EB] font-display font-bold text-sm">
                <td className="px-4 py-3">Summe {mode === "with" ? "ab aktuellem Monat" : "aller offenen Fälligkeiten"}</td>
                <td colSpan={2}></td>
                <td className="px-4 py-3 text-right text-[#0AA09E]" data-testid="due-total">
                  {formatEUR(mode === "with" ? sumWithRoll : sumWithoutRoll)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="px-4 py-2.5 bg-[#F8FAFC] border-t border-[#E5E7EB] text-[11px] text-[#5B6371]">
          {mode === "with"
            ? "Rollierend: überfällige Vormonate werden in den aktuellen Monat verschoben — realistischer Cashflow-Blick."
            : "Rein: jede Rechnung zählt im Monat wo sie laut Zahlungsziel eigentlich fällig war — hilfreich für Rückschau/Analyse."}
        </div>
      </div>
    </section>
  );
}


// ------------------------------------------------------------------
// Einklappbare Liquiditätsvorschau
// ------------------------------------------------------------------
function CollapsibleLiquidityForecast({ data }) {
  const [open, setOpen] = useState(true);
  const cm = data.current_month;
  const t = data.totals;
  return (
    <section data-testid="liquidity-forecast">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-end justify-between mb-3 gap-3 cursor-pointer group focus:outline-none"
        data-testid="liquidity-forecast-toggle"
        aria-expanded={open}
      >
        <div className="text-left">
          <div className="label-eyebrow flex items-center gap-2">
            {open ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
            Liquiditätsvorschau
          </div>
          <h2 className="font-display text-2xl font-bold mt-1 group-hover:text-[#0AA09E] transition-colors">
            Erwartete Einnahmen pro Monat
          </h2>
          <p className="text-xs text-[#5B6371] mt-1">
            Basis: Rechnungsdatum + Zahlungsziel (z.B. 20 Tage bis Geld vom Kunden / Jobcenter / BA kommt) · <span className="text-[#0AA09E] underline">{open ? "einklappen" : "aufklappen"}</span>
          </p>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">Summe offen</div>
          <div className="font-display text-2xl font-bold text-[#0AA09E]">{formatEUR(t.total_expected_open || 0)}</div>
        </div>
      </button>
      {open && (
        <ForecastTable
          forecast={data.forecast || []}
          breakEven={cm.break_even}
          target={cm.revenue_target_monthly}
          currentYm={cm.ym}
        />
      )}
    </section>
  );
}
